import { AddressInputAddress, RecognizedPlace } from 'lib/components/Form/AddressInput/AddressInput'
import type { AutoCompleteItem as ComponentAutoCompleteItem } from 'lib/types/AutoCompleteInput'

import { AutoCompleteItem, GooglePlace, PlaceDetails } from '@/types/autoComplete'
import {
  createAddressInputAddress,
  createPlaceDetailAddress,
  createRecognizedPlace,
} from '@/utils/addressInput'

type AnyAutoCompleteItem = ComponentAutoCompleteItem | AutoCompleteItem

const normalizeAutoCompleteItem = (item: AnyAutoCompleteItem): AutoCompleteItem => {
  const address = (item as AnyAutoCompleteItem).address

  const formattedAddress =
    (item as { formattedAddress?: string }).formattedAddress ??
    (item as { formatted_address?: string }).formatted_address ??
    (item as AnyAutoCompleteItem).name

  const iconUrl = (item as { iconUrl?: string }).iconUrl ?? (item as { icon_url?: string }).icon_url

  const normalizedAddress = address
    ? {
        address1:
          (address as { address1?: string; address_1?: string }).address1 ??
          (address as { address1?: string; address_1?: string }).address_1 ??
          '',
        address2:
          (address as { address2?: string; address_2?: string }).address2 ??
          (address as { address2?: string; address_2?: string }).address_2 ??
          '',
        city: (address as { city?: string }).city || '',
        state: (address as { state?: string }).state || '',
        zip: (address as { zip?: string }).zip || '',
        countryCode:
          (address as { countryCode?: string; country_code?: string }).countryCode ??
          (address as { countryCode?: string; country_code?: string }).country_code ??
          '',
      }
    : undefined

  return {
    id: (item as AnyAutoCompleteItem).id,
    name: (item as AnyAutoCompleteItem).name,
    type: (item as AnyAutoCompleteItem).type ?? '',
    iconUrl,
    formattedAddress,
    address: normalizedAddress,
    record: (item as AnyAutoCompleteItem).record,
  }
}

export interface ProcessAutocompleteResult {
  address: AddressInputAddress | null
  placeId: string | null
  recognizedPlace: RecognizedPlace | null
}
export const processAutoComplete = (
  value?: string | number | ComponentAutoCompleteItem | undefined,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  name?: string | undefined,
): ProcessAutocompleteResult | null => {
  if (
    value == undefined ||
    typeof value === 'number' ||
    (typeof value == 'string' && value == '')
  ) {
    return null
  }

  const item = normalizeAutoCompleteItem(value as AnyAutoCompleteItem)

  if (item && item.type == 'Club') {
    const place = createRecognizedPlace(item)

    if (item.record != undefined) {
      const newAddress = createAddressInputAddress(item)
      return {
        address: newAddress,
        placeId: null,
        recognizedPlace: place,
      }
    }
    return { address: null, placeId: null, recognizedPlace: place }
  }

  if (item?.type == 'GooglePlace') {
    /**
     * trigger the place detail call
     */
    const place: GooglePlace = value as GooglePlace
    return {
      address: null,
      placeId: place.id,
      recognizedPlace: null,
    }
  }
  return null
}

export const formatAutoCompleteOption = (option: AnyAutoCompleteItem): string => {
  const normalized = normalizeAutoCompleteItem(option)
  if (normalized?.address?.city)
    return [
      `${normalized.name} <br>`,
      `${normalized.address?.city},`,
      normalized.address?.state,
      normalized.address?.zip,
    ].join(' ')
  else
    return (
      (normalized as { formatted_address?: string }).formatted_address ||
      normalized?.formattedAddress ||
      ''
    )
}

// google requires that we use the same session token from the autocomplete queries and
// clear it on the place details call to reduce billing expenses
const SESSION_TOKEN_KEY = 'google_places_session_token'

export const autoCompleteSessionToken = (): string => {
  // Check if we already have a token in sessionStorage
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return ''
  }
  const existingToken = sessionStorage.getItem(SESSION_TOKEN_KEY)

  if (existingToken) {
    return existingToken
  }

  // Generate a new token if none exists
  const now = new Date()
  const token = Math.random().toString(36).substring(2) + now.getTime().toString(36)

  // Save to sessionStorage
  sessionStorage.setItem(SESSION_TOKEN_KEY, token)

  return token
}

export const clearAutoCompleteSessionToken = (): void => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return
  }
  sessionStorage.removeItem(SESSION_TOKEN_KEY)
}

export const processPlaceDetail = (placeDetails: PlaceDetails): AddressInputAddress | null => {
  if (placeDetails) {
    // Clear autocomplete session token after processing place details
    clearAutoCompleteSessionToken()
    return createPlaceDetailAddress(placeDetails)
  }
  return null
}
